import { forwardRef, memo, type ReactNode, type ReactElement, type ButtonHTMLAttributes } from 'react'
import { useTranslation } from 'react-i18next'

/** Button variant options */
type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'success' | 'warning' | 'ghost' | 'link' | 'construction'

/** Button size options */
type ButtonSize = 'sm' | 'md' | 'lg' | 'xl'

/** Props for Button component */
interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  children?: ReactNode
  variant?: ButtonVariant
  size?: ButtonSize
  fullWidth?: boolean
  loading?: boolean
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
  className?: string
  leftIcon?: ReactNode
  rightIcon?: ReactNode
}

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-gradient-primary text-white shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] focus:ring-primary-500 transition-all duration-200',
  secondary: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 focus:ring-gray-400 active:bg-gray-300 transition-colors duration-200',
  danger: 'bg-gradient-danger text-white shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] focus:ring-muted-red transition-all duration-200',
  success: 'bg-gradient-success text-white shadow-md hover:shadow-glow-success hover:scale-[1.02] active:scale-[0.98] focus:ring-muted-green transition-all duration-200',
  warning: 'bg-gradient-warning text-white shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] focus:ring-muted-amber transition-all duration-200',
  ghost: 'bg-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus:ring-gray-400 transition-colors duration-200',
  link: 'bg-transparent text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline p-0 min-h-0 transition-colors duration-200',
  construction: 'bg-gradient-construction text-white shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] focus:ring-construction-orange transition-all duration-200',
}

const sizes: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm min-h-[36px]',
  md: 'px-4 py-2 text-base min-h-[44px]',
  lg: 'px-6 py-3 text-lg min-h-[52px]',
  xl: 'px-8 py-4 text-xl min-h-[60px]'
}

/** Internal spinner component for loading state */
function ButtonSpinner({ className = '' }: { className?: string }): ReactElement {
  return (
    <svg
      className={`animate-spin h-5 w-5 ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )
}

const Button = memo(forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    loading = false,
    disabled = false,
    type = 'button',
    className = '',
    leftIcon,
    rightIcon,
    ...props
  },
  ref
) {
  const { t } = useTranslation()
  const baseStyles = 'inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'

  const variantStyles = variants[variant] ?? variants.primary
  const sizeStyles = sizes[size] ?? sizes.md
  const widthStyles = fullWidth ? 'w-full' : ''

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={`${baseStyles} ${variantStyles} ${sizeStyles} ${widthStyles} ${className}`}
      {...props}
    >
      {loading ? (
        <>
          <ButtonSpinner className="mr-2" />
          <span>{t('common.loading')}</span>
        </>
      ) : (
        <>
          {leftIcon && <span className="mr-2">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="ml-2">{rightIcon}</span>}
        </>
      )}
    </button>
  )
}))

export default Button
